import React from 'react';
import Popover from './Popover';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTag, faClock, faCalendarDay, faCalendar, faPersonChalkboard,
    faBookOpenReader, faLocationDot, faSchool, faChair, faCopy, faBookOpen, faClockRotateLeft
} from '@fortawesome/free-solid-svg-icons';

import {
    getDaysOfWeek,
    formatTime,
    formatDateRange,
    createMarkup, formatDateRangeDuration
} from '../utils/formatters';

function CRNSectionCard({ crn, sections, onlinePop, setDrawerVisible, setSelectedCRN, setInstructorDrawerOpen, setInstructorData }) {
    const item = sections[0];
    const instructorName = item.pfaName;
    const instructorEmail = item.pfaEmail;
    const secondaryInsturctorNames = item?.sfaName?.split(',');
    const secondaryInsturctorEmails = item?.sfaEmail?.split(',');
    const sortedSections = [...sections].sort((a, b) => {
        const aHasTime = !!a.beginTime;
        const bHasTime = !!b.beginTime;


        if (aHasTime && !bHasTime) return -1;
        if (!aHasTime && bHasTime) return 1;
        if (!aHasTime && !bHasTime) return 0;


        const aDate = a.ptrmStartDate;
        const bDate = b.ptrmStartDate;

        if (aDate && bDate) {
            const dateComparison = aDate.localeCompare(bDate);
            if (dateComparison !== 0) {
                return dateComparison;
            }
        } else if (aDate) {
            return -1;
        } else if (bDate) {
            return 1;
        }

        return a.beginTime.localeCompare(b.beginTime);
    });

    const labelClass = "font-normal text-gray-600 text-xs mr-1";
    const valueClass = "text-xs font-medium text-gray-800 capitalize";
    const popoverLinkClass = "text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer hover:cursor-lancer capitalize";

    // Clipboard copy handler with feedback
    const handleCopy = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            alert(`Copied: ${text}`);
        });
    };

    return (
        <div key={crn} className={`px-4 py-3 border-t border-gray-200 bg-white`}>
            <div className="grid grid-cols-1 md:grid-cols-[80px_1.7fr_2.0fr_1.5fr_2.0fr_1.5fr_1.5fr_1.5fr] gap-x-4 gap-y-2">

                {/* Col 0: Status */}
                <div className="space-y-1.5">
                    <div className="flex items-center min-h-8">
                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${item.fwSectOpenInd === 'C' ? 'bg-red-100 text-red-700' :
                            item.fwSectOpenInd === 'O' ? 'bg-green-100 text-green-700' :
                                item.fwSectOpenInd === 'W' ? 'bg-blue-100 text-blue-700' :
                                    item.fwSectOpenInd === 'R' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-gray-100 text-gray-600'
                            }`}>
                            {item.fwSectOpenInd === 'C' ? 'Closed' :
                                item.fwSectOpenInd === 'O' ? 'Open' :
                                    item.fwSectOpenInd === 'W' ? 'Waitlist' :
                                        item.fwSectOpenInd === 'R' ? 'Reserved' :
                                            'Unknown'}
                        </span>
                    </div>
                    {item.saprDesc || item.cohortDesc ?
                        <div>
                            <Popover
                                content={
                                    <div className="text-xs space-y-1">
                                        {item.saprDesc && <div> {item.saprDesc}</div>}
                                        {item.cohortDesc && <div><strong>Cohort:</strong> {item.cohortDesc}</div>}
                                    </div>
                                }
                                description="Restricted"
                                triggerClassName={popoverLinkClass}
                            >
                                <div className="inline-flex items-center space-x-1">
                                    <span className="bg-amber-100 text-amber-800 font-medium px-2 py-1 rounded-full">Restriction</span>
                                </div>
                            </Popover>
                        </div> :
                        <div></div>
                    }
                </div>


                {/* Col 1: CRN + Meeting Times */}
                <div className="space-y-1.5">
                    <div className="flex items-center min-h-8">
                        <FontAwesomeIcon icon={faTag} className="mr-1.5 text-gray-500 w-3.5" />
                        <span className={labelClass}>CRN:</span>
                        <Popover
                            content={
                                <div className="p-2 text-white text-xs space-y-1">
                                    {[
                                        { label: "Date to Enroll", value: formatDateRange(item.lastDateToEnroll) },
                                        { label: "Last day to add class", value: formatDateRange(item.lastDateToEnroll) },
                                        { label: "Last day to drop with a refund", value: formatDateRange(item.refundDate) },
                                        { label: "Census Date", value: formatDateRange(item.censusDate) },
                                        { label: "Last day to declare grade mode", value: formatDateRange(item.grademodeDate) },
                                        { label: "Last day to drop without a \"W\"", value: formatDateRange(item.dropNoWDate) },
                                        { label: "Last day to drop with a \"W\"", value: formatDateRange(item.dropWDate) },
                                    ].map((d, i) => (
                                        <div key={i} className="flex justify-between items-start">
                                            <span className="mr-2 whitespace-nowrap">{d.label}:</span>
                                            <span className="text-right">{d.value}</span>
                                        </div>
                                    ))}
                                </div>
                            }
                            description="Critical Dates for this Course"
                            triggerClassName={popoverLinkClass}
                        >
                            <span
                                onClick={() => {
                                    setSelectedCRN(item);
                                    setDrawerVisible(true);
                                }}
                            >
                                {crn}
                            </span>
                        </Popover>
                    </div>
                    {/* Meeting Times for Desktop */}
                    <div className="space-y-1.5 md:block hidden">
                        {sortedSections.map((sec, i) => (
                            <div key={i}>
                                <FontAwesomeIcon icon={faClock} className="mr-1.5 text-gray-500 w-3.5" />
                                <span className={labelClass}>Time:</span>
                                <span className={valueClass}>
                                    {sec.beginTime && sec.endTime
                                        ? `${formatTime(sec.beginTime)} - ${formatTime(sec.endTime)}`
                                        : `${sec.weeklyHrs} Hrs/Wk of online work`}
                                </span>
                            </div>
                        ))}
                    </div>
                    {/* <div className="space-y-1.5">
                        <FontAwesomeIcon icon={faGraduationCap} className="mr-1.5 text-gray-500 w-3.5" />
                        <span className={labelClass}>Units:</span>
                        <span className={valueClass}>{item.creditHrLow}</span>
                    </div> */}
                </div>


                {/* Mobile */}

                <div className="md:hidden col-span-full space-y-2 mt-2">

                    {/* Mobile-only: Instructor (with drawer and secondary instructors) */}
                    <div className="flex items-center text-sm text-gray-700">
                        <FontAwesomeIcon icon={faPersonChalkboard} className="mr-1.5 text-gray-500 w-3.5" />
                        <span className="mr-1">Instructor:</span>
                        {instructorName.toLowerCase() === 'staff' ? (
                            <span className="text-xs font-medium text-gray-800">Staff</span>
                        ) : (
                            <span
                                onClick={() => {
                                    setInstructorDrawerOpen(true);
                                    setInstructorData({
                                        primary: { name: instructorName, email: instructorEmail },
                                        secondary: secondaryInsturctorNames?.map((name, idx) => ({
                                            name: name.trim(),
                                            email: secondaryInsturctorEmails?.[idx]?.trim()
                                        }))
                                    });
                                }}
                                className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer capitalize"
                            >
                                {instructorName}
                            </span>
                        )}
                        {secondaryInsturctorNames?.length > 0 && (
                            <span
                                onClick={() => {
                                    setInstructorDrawerOpen(true);
                                    setInstructorData({
                                        primary: { name: instructorName, email: instructorEmail },
                                        secondary: secondaryInsturctorNames?.map((name, idx) => ({
                                            name: name.trim(),
                                            email: secondaryInsturctorEmails?.[idx]?.trim()
                                        }))
                                    });
                                }}
                                className="ml-2 text-xs text-blue-600 hover:underline cursor-pointer"
                            >
                                +{secondaryInsturctorNames.length} more
                            </span>
                        )}
                    </div>

                    {/* Mobile-only: Units */}
                    <div className="flex items-center text-sm text-gray-700">
                        <FontAwesomeIcon icon={faBookOpen} className="mr-1.5 text-gray-500 w-3.5" />
                        Units: {item.creditHrLow}
                    </div>
                    {/* Mobile-only: Location */}
                    <div className="flex items-center text-sm text-gray-700">
                        <FontAwesomeIcon icon={faLocationDot} className="mr-1.5 text-gray-500 w-3.5" />
                        Location: {item.campDesc || 'N/A'}
                    </div>
                    {/* Mobile-only: Term */}
                    <div className="flex items-center text-sm text-gray-700">
                        <FontAwesomeIcon icon={faClockRotateLeft} className="mr-1.5 text-gray-500 w-3.5" />
                        Term: {item.ptrmDesc}
                    </div>

                    {/* Grouped Meeting Times for Mobile */}
                    {sortedSections.map((sec, i) => (
                        <div key={i} className="flex flex-col gap-y-1.5 p-2 rounded-md border bg-gray-50">
                            {sec.beginTime && sec.endTime ? (
                                <div className="flex items-center text-sm text-gray-700">
                                    <FontAwesomeIcon icon={faClock} className="mr-1.5 text-gray-500 w-3.5" />
                                    Time: {formatTime(sec.beginTime)} - {formatTime(sec.endTime)}
                                </div>
                            ) : (
                                <div className="flex items-center text-sm text-gray-700">
                                    <FontAwesomeIcon icon={faClock} className="mr-1.5 text-gray-500 w-3.5" />
                                    Time: {sec.weeklyHrs} Hrs/Wk of online work
                                </div>
                            )}
                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faCalendarDay} className="mr-1.5 text-gray-500 w-3.5" />
                                Day(s): {getDaysOfWeek(sec) || 'Online/TBA'}
                            </div>
                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faBookOpenReader} className="mr-1.5 text-gray-500 w-3.5" />
                                Format: {sec.schdDesc || 'N/A'}
                            </div>
                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faSchool} className="mr-1.5 text-gray-500 w-3.5" />
                                Room: {sec.stvbldgDesc} {sec.roomCode === 'Needs Assignment' ? '' : `- ${sec.roomCode}`}
                            </div>

                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faCalendar} className="mr-1.5 text-gray-500 w-3.5" />
                                Duration: {formatDateRangeDuration(sortedSections[i].ptrmStartDate, sortedSections[i].ptrmEndDate)}
                            </div>
                        </div>
                    ))}


                </div>


                {/* Col 2: Instructor + Meeting Days */}
                <div className="space-y-1.5 md:block hidden">
                    <div className="flex items-center min-h-8">
                        <FontAwesomeIcon icon={faPersonChalkboard} className="mr-1.5 text-gray-500 w-3.5" />
                        <span className={labelClass}>Instructor:</span>

                        {instructorName.toLowerCase() === 'staff' ? (
                            <span className={valueClass}>Staff</span>
                        ) : (
                            <Popover
                                content={<span className="text-xs">Name: {instructorName}<br />Email: {instructorEmail}</span>}
                                description="Instructor Information"
                                triggerClassName={popoverLinkClass}
                            >
                                <div className="inline-flex items-center space-x-1">
                                    <span>{instructorName}</span>
                                    <FontAwesomeIcon
                                        icon={faCopy}
                                        className="text-gray-500 cursor-pointer hover:text-blue-600"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopy(instructorEmail);
                                        }}
                                    />
                                </div>
                            </Popover>
                        )}

                        {/* Secondary Instructors Drawer Trigger */}
                        {secondaryInsturctorNames?.length > 0 && (
                            <span
                                onClick={() => {
                                    setInstructorDrawerOpen(true);
                                    setInstructorData({
                                        primary: { name: instructorName, email: instructorEmail },
                                        secondary: secondaryInsturctorNames?.map((name, idx) => ({
                                            name: name.trim(),
                                            email: secondaryInsturctorEmails?.[idx]?.trim()
                                        }))
                                    });
                                }}
                                className="ml-2 text-xs text-blue-600 hover:underline cursor-pointer"
                            >
                                +{secondaryInsturctorNames.length} more
                            </span>
                        )}
                    </div>
                    <div className="space-y-1.5">
                        {sortedSections.map((sec, i) => (
                            <div key={i}>
                                <FontAwesomeIcon icon={faCalendarDay} className="mr-1.5 text-gray-500 w-3.5" />
                                <span className={labelClass}>Day(s):</span>
                                <span className={valueClass}>{getDaysOfWeek(sec) || 'Online/TBA'}</span>
                            </div>
                        ))}
                    </div>
                </div>


                {/* Col 3: Credit + Format */}
                <div className="space-y-1.5 md:block hidden">
                    <div className="flex items-center min-h-8">
                        <FontAwesomeIcon icon={faBookOpen} className="mr-1.5 text-gray-500 w-3.5" />
                        {/* <span className={labelClass}>Course Type:</span>
                        <span className={valueClass}>{item.creditHrLow > 0 ? 'Credit' : 'Non-Credit'}</span> */}
                        <span className={labelClass}>Units:</span>
                        <span className={valueClass}>{item.creditHrLow}</span>
                    </div>
                    <div className="space-y-1.5">
                        {sortedSections.map((sec, i) => (
                            <div key={i}>
                                <FontAwesomeIcon icon={faBookOpenReader} className="mr-1.5 text-gray-500 w-3.5" />
                                <span className={labelClass}>Format:</span>
                                <span className={valueClass}>{sec.schdDesc || 'N/A'}</span>
                            </div>
                        ))}
                    </div>
                </div>


                {/* Col 4: Location + Room */}
                <div className="space-y-1.5 md:block hidden">
                    <div className="flex items-center space-x-1 min-h-8">
                        <FontAwesomeIcon icon={faLocationDot} className="mr-1.5 text-gray-500 w-3.5" />
                        <span className={labelClass}>Location:</span>
                        {item.bldgAddress ? (
                            <Popover
                                content={<span className="text-xs">{item.bldgAddress}</span>}
                                description="Campus Address"
                                triggerClassName={popoverLinkClass}
                            >
                                <div className="inline-flex items-center space-x-1">
                                    <span>{item.campDesc || 'N/A'}</span>
                                    <FontAwesomeIcon
                                        icon={faCopy}
                                        className="text-gray-500 cursor-pointer hover:text-blue-600"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopy(item.bldgAddress);
                                        }}
                                    />
                                </div>
                            </Popover>
                        ) : (
                            <span className={valueClass}>{item.campDesc || 'N/A'}</span>
                        )}
                    </div>
                    <div className="space-y-1.5">
                        {sortedSections.map((sec, i) => (
                            <div key={i}>
                                <FontAwesomeIcon icon={faSchool} className="mr-1.5 text-gray-500 w-3.5" />
                                <span className={labelClass}>Room:</span>
                                {onlinePop.hasOwnProperty(sec.stvbldgDesc) ? (
                                    <Popover
                                        content={<span className="text-xs">{onlinePop[sec.stvbldgDesc]}</span>}
                                        description={sec.stvbldgDesc || 'Online Info'}
                                        triggerClassName={popoverLinkClass}
                                    >
                                        {sec.stvbldgDesc}
                                    </Popover>
                                ) : (
                                    <span className={valueClass}>
                                        {sec.stvbldgDesc} {sec.roomCode === 'Needs Assignment' ? '' : `- ${sec.roomCode}`}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Col 5: Term + Duration */}
                <div className="space-y-1.5 md:block hidden">
                    <div className="flex items-center min-h-8">
                        <FontAwesomeIcon icon={faClockRotateLeft} className="mr-1.5 text-gray-500 w-3.5" />
                        <span className={labelClass}>Term:</span>
                        <span className={valueClass}>{item.ptrmDesc}</span>
                    </div>
                    <div className="space-y-1.5">
                        {sortedSections.map((sec, i) => (
                            <div key={i}>
                                <FontAwesomeIcon icon={faCalendar} className="mr-1.5 text-gray-500 w-3.5" />
                                <span className={labelClass}>Duration:</span>
                                <span className={valueClass}>
                                    {formatDateRangeDuration(sec.ptrmStartDate, sec.ptrmEndDate)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Col 6: Seats */}
                <div className="space-y-1">
                    {parseInt(item.seatsAvail) <= 0 && parseInt(item.maxEnrl) > 0 ? (

                        <div className="inline-flex items-center bg-red-100 text-red-700 text-xs font-semibold px-2.5 py-0.5 rounded-full min-h-8">
                            <FontAwesomeIcon icon={faChair} className="mr-1.5 w-3.5" />
                            Seats: Full
                        </div>
                    ) : (

                        <div className="flex items-center min-h-8">
                            <FontAwesomeIcon icon={faChair} className="mr-1.5 text-gray-500 w-3.5" />
                            <span className={labelClass}>Seats:</span>
                            <span className={valueClass}>{item.enrl || 0}/{item.maxEnrl || 0}</span>
                        </div>
                    )}
                    {parseInt(item.maxEnrl) > 0 && (
                        <div className="h-2.5 mt-1 bg-gray-200 rounded-full w-32 overflow-hidden">
                            <div
                                className={`h-2.5 rounded-full ${parseInt(item.enrl) / parseInt(item.maxEnrl) >= 0.90
                                    ? 'bg-red-500'
                                    : parseInt(item.enrl) / parseInt(item.maxEnrl) >= 0.8
                                        ? 'bg-yellow-500'
                                        : 'bg-green-500'
                                    }`}
                                style={{ width: `${Math.min((parseInt(item.enrl || 0) / parseInt(item.maxEnrl || 1)) * 100, 100)}%` }}
                            ></div>
                        </div>
                    )}
                    {parseInt(item.waitAvail) > 0 && parseInt(item.waitCapacity) > 0 && (
                        <div className="mt-1.5 inline-flex items-center bg-blue-100 text-blue-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                            Waitlist: {item.waitCount || 0}/{item.waitCapacity || 0}
                        </div>
                    )}
                    {parseInt(item.seatsAvail) <= 0 && parseInt(item.waitCapacity) > 0 && (
                        <div className="h-2.5 mt-1 bg-gray-200 rounded-full w-32 overflow-hidden">
                            <div
                                className="h-2.5 bg-blue-500 rounded-full"
                                style={{ width: `${Math.min((parseInt(item.waitCount || 0) / parseInt(item.waitCapacity || 1)) * 100, 100)}%` }}
                            ></div>
                        </div>
                    )}
                </div>


                {/* Col 7: Badges */}
                <div className="flex flex-wrap items-start justify-start md:justify-end gap-1.5 text-xs">
                    {(item.attrchrts || '').split(',').map(a => a.trim()).filter(Boolean).map(attr => {
                        let badgeStyle = "font-medium px-2 py-1 rounded-full";
                        if (attr === 'UC') return <span key={attr} className={`bg-purple-100 text-purple-800 ${badgeStyle}`}>Transferable UC</span>;
                        if (attr === 'CSU') return <span key={attr} className={`bg-indigo-100 text-indigo-800 ${badgeStyle}`}>Transferable CSU</span>;
                        if (attr === 'ZTC') return <span key={attr} className={`bg-green-100 text-green-800 ${badgeStyle}`}>ZTC</span>;
                        if (attr === 'QR') return <span key={attr} className={`bg-sky-100 text-sky-800 ${badgeStyle}`}>QR</span>;
                        return null;
                    })}
                </div>
            </div>


            {/* Section footnotes for all meetings, deduplicated */}
            {
                (() => {
                    const uniqueFootnotes = [...new Set(sortedSections
                        .map(sec => sec.sectionFootnotes)
                        .filter(f => f && f.trim()))];
                    return uniqueFootnotes.length > 0 ? (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                            {uniqueFootnotes.map((footnote, i) => (
                                <strong key={i}><span className="text-xs text-gray-600 block" dangerouslySetInnerHTML={createMarkup(footnote)}></span></strong>
                            ))}
                        </div>
                    ) : null;
                })()
            }

        </div >
    );
}

export default CRNSectionCard;